import React from "react";
import { Row, TableInstance } from "react-table";
import { Table } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { useSearchParams } from "react-router-dom";

import "./DataTable.scss";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as Sortable } from "assets/images/icons/Sortable.svg";
import { ReactComponent as DragIcon } from "assets/images/icons/DragIcon.svg";
import { ReactComponent as MenuIcon } from "assets/images/icons/Menu.svg";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Pagination from "components/molecules/Pagination/Pagination";
import { FormattedMessage } from "react-intl";

type DataTableProps = {
  withoutBody?: boolean;
  headerLeft?: React.ReactNode;
  headerRight?: React.ReactNode;
  isLoading?: boolean;
  totalData?: number;
  draggable?: boolean;
  onRowClick?: (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
  tableInstance: TableInstance;
  RowSubComponent?: any;
  customNoResults?: string;
  isProcessing?: boolean;
  isLoadingMoreData?: boolean;
  autoPagination?: boolean;
  onColumnOrderChange?: (newOrder: string[]) => void;
  rowRenderer?: (row: Row<any>) => React.ReactNode;
};

function DataTable(props: DataTableProps) {
  const {
    withoutBody,
    headerLeft,
    headerRight,
    tableInstance,
    isLoading,
    totalData,
    draggable,
    onRowClick,
    RowSubComponent,
    customNoResults,
    isProcessing,
    isLoadingMoreData,
    autoPagination,
    onColumnOrderChange,
    rowRenderer,
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    columns,
    setColumnOrder,
    pageCount,
    state,
    setPageSize,
    visibleColumns,
  } = tableInstance;
  const dataRows = autoPagination ? page : rows;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const columnIds = columns.filter((item) => item.isVisible).map((item) => item.id);
    setColumnOrder((prev) => {
      let previousOrder = [...prev];
      if (previousOrder.length && previousOrder.length !== columnIds.length) {
        columnIds.forEach((columnId) => {
          if (!previousOrder.includes(columnId)) {
            previousOrder.push(columnId);
          }
        });
        previousOrder = previousOrder.filter((columnId) => columnIds.includes(columnId));
      }
      const cloned = previousOrder.length ? [...previousOrder] : [...columnIds];
      const [removed] = cloned.splice(result.source.index, 1);
      cloned.splice(result?.destination?.index || 0, 0, removed);
      if (onColumnOrderChange) {
        onColumnOrderChange(cloned);
      }
      return cloned;
    });
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={`data-table ${withoutBody ? "no-body" : ""}`}>
        {(headerLeft || headerRight) && (
          <div className="d-flex align-items-center justify-content-between mb-3">
            {headerLeft || <div />}
            {headerRight || <div />}
          </div>
        )}

        <Droppable droppableId="draggable-table" direction="horizontal" mode="virtual">
          {(droppableProvided) => (
            <div className="dropppable-container" ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
              <Table className="no-wrap" id="data-grid" responsive {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, index) => (
                        <Draggable
                          key={column.id}
                          draggableId={column.id}
                          index={index}
                          isDragDisabled={!draggable || column.disableDrag}
                        >
                          {(provided, snapshot) => (
                            <th
                              className={`font-b4 ${snapshot.isDragging ? "dragging" : ""} ${
                                column.sortable ? "sortable" : ""
                              }`}
                              {...column.getHeaderProps(
                                column.sortable
                                  ? column.getSortByToggleProps({ title: `Sort by ${column.Header}` })
                                  : undefined,
                              )}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  {column.render("Header")}
                                  <span className="ml-2 table-sort-icon">
                                    {column.isSorted &&
                                      (column.isSortedDesc ? <SortIcon /> : <SortIcon transform="rotate(180)" />)}
                                    {!column.isSorted && column.sortable && (
                                      <Sortable width={6} height={14} fill="#7e8589" />
                                    )}
                                  </span>
                                </div>
                                {draggable && !column.disableDrag && (
                                  <div className="drag-icon" {...provided.dragHandleProps}>
                                    <DragIcon />
                                  </div>
                                )}
                              </div>
                            </th>
                          )}
                        </Draggable>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {dataRows.length > 0 &&
                    !isLoading &&
                    dataRows.map((row) => {
                      prepareRow(row);
                      const rowProps = row.getRowProps();
                      return (
                        <React.Fragment key={rowProps.key}>
                          {rowRenderer && rowRenderer(row)}
                          <tr
                            {...row.getRowProps()}
                            onClick={(e) => (onRowClick ? onRowClick(row, e) : null)}
                            className={onRowClick ? "clickable-row" : ""}
                          >
                            {row.cells.map((cell) => (
                              <td
                                className={`font-b2 table-item ${state.pageSize === 100 ? "compact" : ""}`}
                                style={{
                                  // @ts-ignore
                                  border: row.isExpanded && "none",
                                  ...(cell.column.minWidth && { minWidth: cell.column.minWidth }),
                                  ...(cell.column.maxWidth && { maxWidth: cell.column.maxWidth }),
                                  ...(cell.column.actions && { textAlign: "right" }),
                                  ...(cell.column.style && cell.column.style),
                                }}
                                {...cell.getCellProps()}
                              >
                                {cell.column?.actions ? (
                                  <div style={{ minWidth: 70 }}>
                                    <div className="menu-icon w-100">
                                      <MenuIcon />
                                    </div>
                                    <div className="action-content">{cell.render("Cell")}</div>
                                  </div>
                                ) : (
                                  cell.render("Cell")
                                )}
                              </td>
                            ))}
                          </tr>
                          {/* @ts-ignore */}
                          {row.isExpanded && RowSubComponent ? (
                            <tr>
                              <td
                                style={{ backgroundColor: "#F7F9FA", borderRadius: "8px", border: "none" }}
                                className="sub-row"
                                colSpan={visibleColumns.length}
                              >
                                <RowSubComponent row={row} />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  {isProcessing && (
                    <tr>
                      <td className="" colSpan={columns.length}>
                        <div className="d-flex align-items-center w-100 justify-content-end table-spinner mt-5">
                          <p className="loading-spinner">
                            <div className="spinner-border spinner-border-sm" role="status" />
                          </p>
                          <p className="ml-3">Fetching Quotations</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {isLoadingMoreData &&
                    !isProcessing &&
                    Array(1)
                      .fill(1)
                      .map((_, index) => (
                        <tr key={index}>
                          <td className="pt-0 pb-0" colSpan={columns.length}>
                            <LoadingSkeleton rows={1} bodyClasses="p-0" className="p-0" noShadow />
                          </td>
                        </tr>
                      ))}

                  {isLoading &&
                    !isLoadingMoreData &&
                    Array(4)
                      .fill(1)
                      .map((_, index) => (
                        <tr key={index}>
                          <td className="pt-0 pb-0" colSpan={columns.length}>
                            <LoadingSkeleton rows={1} bodyClasses="p-0" className="p-0" noShadow />
                          </td>
                        </tr>
                      ))}

                  {!isLoading && !isProcessing && !dataRows.length && (
                    <tr>
                      <td className="no-results" colSpan={columns.length}>
                        <span>
                          {customNoResults || (
                            <FormattedMessage
                              id="components.organisms.listing.noResultsFound"
                              defaultMessage="No results found. Try adjusting your search or filter."
                            />
                          )}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
                {droppableProvided.placeholder}
              </Table>
            </div>
          )}
        </Droppable>
        {!isLoading && totalData && (autoPagination || tableInstance.manualPagination) ? (
          <Pagination
            showingDataCount={dataRows.length}
            totalData={totalData}
            pageSize={state.pageSize}
            setPageSize={setPageSize}
            className="d-flex align-items-center justify-content-center font-b4 px-3"
            totalPages={pageCount} // 21 pageCount from instance
            currentPage={state.pageIndex + 1} // 1 pageIndex + 1
            recordEnd={state.pageIndex * state.pageSize + dataRows.length}
            recordStart={state.pageIndex * state.pageSize + 1}
            pageClicked={(ele) => {
              searchParams.set("page", ele);
              setSearchParams(searchParams);
            }}
            showingResult
          />
        ) : null}
      </div>
    </DragDropContext>
  );
}

export default DataTable;

DataTable.defaultProps = {
  withoutBody: false,
  headerLeft: null,
  headerRight: null,
  isLoading: false,
  totalData: null,
  draggable: true,
  onRowClick: null,
  RowSubComponent: null,
  customNoResults: null,
  isProcessing: false,
  isLoadingMoreData: false,
  autoPagination: false,
  onColumnOrderChange: undefined,
  rowRenderer: undefined,
};
